/**
 * Debounced auto-save that never fails quietly (SDET, PR #642): a rejected
 * save leaves the draft unsaved — `state` says 'failed' until a later save
 * lands — `retry` sends the latest draft again, and the tab warns before
 * closing while anything is unsaved. The canvas used to swallow the
 * rejection and count on the next edit, so a failed LAST save kept the
 * edits on screen while reopening restored the older draft.
 *
 * One save in flight at a time: a draft that arrives (or a retry pressed)
 * while one is open waits for it, then the newest goes — two open PATCHes
 * could land out of order and leave the server on the older draft while
 * this reads 'saved' (SDET, PR #690). Leaving the page (unmount) sends a
 * draft still waiting on the debounce rather than dropping it.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export type SaveState = 'saved' | 'pending' | 'failed'

export function useDraftAutosave<T>(save: (draft: T) => Promise<unknown>,
                                    delayMs = 1500) {
  const [state, setState] = useState<SaveState>('saved')
  const latest = useRef<T | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlight = useRef(false)
  const again = useRef(false)
  const mounted = useRef(true)
  const saveRef = useRef(save)
  saveRef.current = save

  const flush = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    const draft = latest.current
    if (draft === null) return
    if (inFlight.current) { again.current = true; return }
    inFlight.current = true
    const settle = (ok: boolean) => {
      inFlight.current = false
      if (latest.current === draft) {
        if (ok) latest.current = null
        if (mounted.current) setState(ok ? 'saved' : 'failed')
      }
      // a newer draft (or a retry) waited for this one: send it now
      if (again.current) { again.current = false; flush() }
    }
    saveRef.current(draft).then(() => settle(true), () => settle(false))
  }, [])

  const schedule = useCallback((draft: T) => {
    latest.current = draft
    setState('pending')
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(flush, delayMs)
  }, [flush, delayMs])

  const retry = useCallback(() => {
    if (latest.current === null) return     // nothing unsaved to send
    setState('pending')
    flush()
  }, [flush])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (timer.current) flush()
    }
  }, [flush])

  useEffect(() => {
    if (state === 'saved') return
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [state])

  return { state, schedule, retry }
}
